/**
 * Shared form-validation helpers for the admin form web components
 * (<client-user-form>, <admin-user-form>, <accommodation-form>).
 *
 * Built on the native Constraint Validation API: callers express what they can
 * in markup (required, type, minlength, maxlength, pattern …) and, for rules
 * the markup can't express (e.g. slug uniqueness), set
 * `element.setCustomValidity( msg )` before validating.
 *
 * `validateScope()` then runs `checkValidity()` on every field in a scope (a
 * <form> or a single wizard step panel). For each invalid field it marks the
 * control with `.is-invalid` (reddish border) and renders a hint right below
 * the field (`.field__error`). Messages are derived from the failing
 * ValidityState and translated via the dictionary; they can be overridden per
 * field via `data-msg` / `data-msg-required` / `data-msg-pattern` (whose
 * markup is itself translated in the template).
 */

import { _lcs } from "../../i18n";

// Derive a hint from a field's current (failing) validity state.
export function validationMessage( el )
{
    const v = el.validity;
    const ds = el.dataset;
    if ( v.valid ) return "";
    if ( v.customError ) return el.validationMessage;
    if ( v.valueMissing ) return ds.msgRequired || ds.msg || _lcs( "Dieses Feld ist erforderlich." );
    if ( v.typeMismatch )
    {
        if ( el.type === "email" ) return _lcs( "Bitte gib eine gültige E-Mail-Adresse ein." );
        if ( el.type === "url" ) return _lcs( "Bitte gib eine gültige URL ein (inkl. https://)." );
        return ds.msg || _lcs( "Bitte überprüfe das Format dieser Eingabe." );
    }
    if ( v.tooShort ) return ds.msg || _lcs( "Bitte mindestens {count} Zeichen eingeben.", { count: el.minLength } );
    if ( v.tooLong ) return ds.msg || _lcs( "Bitte höchstens {count} Zeichen eingeben.", { count: el.maxLength } );
    if ( v.patternMismatch ) return ds.msgPattern || ds.msg || el.title || _lcs( "Bitte überprüfe das Format dieser Eingabe." );
    return el.validationMessage || _lcs( "Ungültige Eingabe." );
}

// Show the error UI for one field: reddish control + hint below it.
export function markInvalid( el, message )
{
    const field = el.closest( ".field" );
    const control = el.closest( ".field__control" ) || field;
    if ( control ) control.classList.add( "is-invalid" );
    if ( !field ) return;

    let hint = field.querySelector( ".field__error" );
    if ( !hint )
    {
        hint = document.createElement( "p" );
        hint.className = "field__error";
        hint.setAttribute( "role", "alert" );
        field.appendChild( hint );
    }
    hint.textContent = message;
    hint.hidden = false;
}

// Clear the error UI for one field.
export function clearInvalid( el )
{
    const field = el.closest( ".field" );
    const control = el.closest( ".field__control" ) || field;
    if ( control ) control.classList.remove( "is-invalid" );
    if ( !field ) return;
    const hint = field.querySelector( ".field__error" );
    if ( hint )
    {
        hint.hidden = true;
        hint.textContent = "";
    }
}

// Validate every constrained field inside `scope`. Returns true when all are
// valid; otherwise marks each invalid field, focuses the first one and returns
// false. Disabled / non-validating fields are skipped.
export function validateScope( scope )
{
    const els = scope.querySelectorAll( "input, select, textarea" );
    let firstInvalid = null;

    els.forEach( ( el ) =>
    {
        if ( !el.willValidate ) return;
        if ( el.checkValidity() )
        {
            clearInvalid( el );
        }
        else
        {
            markInvalid( el, validationMessage( el ) );
            if ( !firstInvalid ) firstInvalid = el;
        }
    } );

    if ( firstInvalid )
    {
        try { firstInvalid.focus(); } catch ( e ) { /* element may be hidden */ }
        return false;
    }
    return true;
}

// Clear all error UI (and any custom validity) inside `scope`. Use when
// (re)opening a form so stale errors don't carry over.
export function resetValidation( scope )
{
    scope.querySelectorAll( "input, select, textarea" ).forEach( ( el ) =>
    {
        el.setCustomValidity( "" );
        clearInvalid( el );
    } );
}

// Wire live clearing: as the user edits a field, drop its error styling and any
// custom validity (a full re-check happens on the next continue / submit).
export function bindLiveClear( scope )
{
    const handler = ( e ) =>
    {
        const el = e.target;
        if ( el && el.matches && el.matches( "input, select, textarea" ) )
        {
            el.setCustomValidity( "" );
            clearInvalid( el );
        }
    };
    scope.addEventListener( "input", handler );
    scope.addEventListener( "change", handler );
}
